import { charsets, type TCollation, type TCharset } from "@/models/table";

enum StorageType {
	VIRTUAL = "VIRTUAL",
	STORED = "STORED",
}

export interface ITableColumn {
	name: string;
	dataType: TColumnDatatypeName;
	dataTypeLength: number | null;
	primaryKey: boolean;
	notNull: boolean;
	unique: boolean;
	binary: boolean;
	unsigned: boolean;
	zeroFill: boolean;
	autoIncrement: boolean;
	generated: boolean;
	charset: TCharset;
	collation: TCollation<TCharset>;
	comment: string | null;
	storageType: StorageType;
	default: string | null;
}

export const columnDatatypes = [
	{
		code: "VARCHAR",
		requiresLength: true,
		initialLength: 45,
		allowsUnsigned: false,
		allowsZeroFill: false,
		allowsBinary: true,
		allowsAutoIncrement: false,
	},
	{
		code: "INT",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: true,
		allowsZeroFill: true,
		allowsBinary: false,
		allowsAutoIncrement: true,
	},
	{
		code: "TEXT",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: false,
		allowsZeroFill: false,
		allowsBinary: true,
		allowsAutoIncrement: false,
	},
	{
		code: "DECIMAL",
		requiresLength: true,
		initialLength: 10,
		allowsUnsigned: true,
		allowsZeroFill: true,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "DATE",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: false,
		allowsZeroFill: false,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "BOOLEAN",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: false,
		allowsZeroFill: false,
		allowsBinary: true,
		allowsAutoIncrement: false,
	},
	{
		code: "FLOAT",
		requiresLength: true,
		initialLength: 10,
		allowsUnsigned: true,
		allowsZeroFill: true,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "DOUBLE",
		requiresLength: true,
		initialLength: 16,
		allowsUnsigned: true,
		allowsZeroFill: true,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "BIGINT",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: true,
		allowsZeroFill: true,
		allowsBinary: false,
		allowsAutoIncrement: true,
	},
	{
		code: "TINYINT",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: true,
		allowsZeroFill: true,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "SMALLINT",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: true,
		allowsZeroFill: true,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "MEDIUMINT",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: true,
		allowsZeroFill: true,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "TIME",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: false,
		allowsZeroFill: false,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "DATETIME",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: false,
		allowsZeroFill: false,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
	{
		code: "TIMESTAMP",
		requiresLength: false,
		initialLength: null,
		allowsUnsigned: false,
		allowsZeroFill: false,
		allowsBinary: false,
		allowsAutoIncrement: false,
	},
] as const;

type TColumnDatatypeName = (typeof columnDatatypes)[number]["code"];

export class Column implements ITableColumn {
	name: string;
	dataType: TColumnDatatypeName;
	dataTypeLength: number | null = null;
	primaryKey: boolean = false;
	notNull: boolean = false;
	unique: boolean = false;
	binary: boolean = false;
	unsigned: boolean = false;
	zeroFill: boolean = false;
	autoIncrement: boolean = false;
	generated: boolean = false;
	private _charset: TCharset = charsets[0].charset;
	private _collation: TCollation<TCharset> = charsets[0].collations[0];
	comment: string | null = null;
	storageType: StorageType = StorageType.VIRTUAL;
	default: string | null = null;

	constructor(tableName: string) {
		this.name = tableName;
		this.dataType = "VARCHAR";
		this.dataTypeLength = 45;
	}

	// charset

	get charset(): TCharset {
		return this._charset;
	}

	set charset(charset: TCharset) {
		const charsetExists = charsets.some((c) => c.charset === charset);

		if (charsetExists) {
			this._charset = charset;
		} else {
			this._charset = charsets[0].charset;
		}
	}

	// collation

	get collation(): TCollation<TCharset> {
		return this._collation;
	}

	set collation(collation: TCollation<TCharset>) {
		if (!this._charset) return;

		const charsetObj = charsets.find(
			(c) => c.charset === this._charset,
		) as unknown as {
			charset: TCharset;
			collations: TCollation<TCharset>;
		};

		if (charsetObj && charsetObj.collations.includes(collation)) {
			this._collation = collation;
		} else {
			this._collation = charsets[0].collations[0];
		}
	}

	createColumnElement(): HTMLDivElement {
		const container = document.createElement("div");
		container.classList.add("eer-table-column-wrapper");

		const iconContainer = document.createElement("div");
		iconContainer.classList.add("eer-table-column-icon");
		if (this.primaryKey) {
			iconContainer.classList.add("eer-table-column-icon-primary-key");
		} else if (this.notNull) {
			iconContainer.classList.add("eer-table-column-icon-not-null");
		} else {
			iconContainer.classList.add("eer-table-column-icon-standard");
		}

		const infoDiv = document.createElement("div");
		infoDiv.classList.add("eer-table-column-info");
		infoDiv.textContent = `${this.name} ${this.dataType}${this.dataTypeLength ? `(${this.dataTypeLength})` : ""}`;

		container.appendChild(iconContainer);
		container.appendChild(infoDiv);

		return container;
	}
}
